import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["country", "locality", "postalCode", "results", "status", "postalStatus"]
  static values = { url: String }

  connect() {
    this.sequence = 0
    this.close()
  }

  disconnect() { this.close() }

  input() {
    this.close()
    this.timer = setTimeout(() => this.search(), 180)
  }

  postalInput() {
    this.close()
    if (/^\d{5}$/.test(this.postalCodeTarget.value.replace(/\s/g, ""))) {
      this.timer = setTimeout(() => this.search({ postal: true }), 180)
    }
  }

  countryChanged() {
    this.localityTarget.value = ""
    this.postalCodeTarget.value = ""
    this.close()
  }

  async search({ postal = false } = {}) {
    this.close()
    const query = (postal ? this.postalCodeTarget : this.localityTarget).value.trim()
    if (!query || this.localityTarget.disabled) return
    const sequence = this.sequence
    this.request = new AbortController()
    const url = new URL(this.urlValue, location.origin)
    url.searchParams.set("country", this.countryTarget.value)
    url.searchParams.set("q", query)
    try {
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("Localities unavailable")
      const items = await response.json()
      if (sequence !== this.sequence || this.localityTarget.disabled) return
      if (postal) {
        if (items.length === 1) {
          this.localityTarget.value = items[0].name
          this.localityTarget.dispatchEvent(new Event("change", { bubbles: true }))
          this.postalStatusTarget.textContent = `Doplněno: ${items[0].name}. Obec můžete přepsat.`
        } else if (!items.length) {
          this.postalStatusTarget.textContent = "PSČ není v nabídce. Obec vyplňte ručně."
        }
        if (items.length <= 1) return
      }
      const field = postal ? this.postalCodeTarget : this.localityTarget
      field.insertAdjacentElement("afterend", this.resultsTarget)
      this.items = items
      this.resultsTarget.replaceChildren()
      items.forEach((item, index) => {
        const option = document.createElement("button")
        option.type = "button"
        option.id = `${this.resultsTarget.id}-${index}`
        option.role = "option"
        option.tabIndex = -1
        option.setAttribute("aria-selected", "false")
        option.className = "block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-orange-pale aria-selected:bg-orange-pale"
        option.textContent = `${item.name} · ${item.postal_code}`
        option.addEventListener("mousedown", event => event.preventDefault())
        option.addEventListener("click", () => this.choose(index))
        this.resultsTarget.append(option)
      })
      this.resultsTarget.hidden = items.length === 0
      field.setAttribute("aria-expanded", String(items.length > 0))
      if (postal) {
        this.postalStatusTarget.textContent = "PSČ patří více obcím. Vyberte obec z nabídky, nebo ji napište ručně."
      } else {
        this.statusTarget.textContent = items.length ? `${items.length} návrhů. Vyberte šipkami a Enterem, nebo pokračujte vlastním názvem.` : "Obec není v nabídce. Můžete ponechat vlastní název a PSČ vyplnit ručně."
      }
    } catch (error) {
      if (error.name !== "AbortError" && sequence === this.sequence) {
        const status = postal ? this.postalStatusTarget : this.statusTarget
        status.textContent = "Návrhy teď nejsou dostupné. Obec i PSČ můžete vyplnit ručně."
      }
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (!this.resultsTarget.hidden && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault()
      this.active = this.active < 0 ? (event.key === "ArrowDown" ? 0 : this.items.length - 1)
        : (this.active + (event.key === "ArrowDown" ? 1 : -1) + this.items.length) % this.items.length
      Array.from(this.resultsTarget.children).forEach((option, index) => option.setAttribute("aria-selected", String(index === this.active)))
      const option = this.resultsTarget.children[this.active]
      event.currentTarget.setAttribute("aria-activedescendant", option.id)
      option.scrollIntoView({ block: "nearest" })
    } else if (event.key === "Enter" && this.active >= 0) {
      event.preventDefault()
      this.choose(this.active)
    }
  }

  choose(index) {
    const item = this.items[index]
    this.localityTarget.value = item.name
    this.postalCodeTarget.value = item.postal_code
    this.localityTarget.focus()
    this.close()
    this.localityTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.postalCodeTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  focusOut(event) {
    if (event.relatedTarget !== this.localityTarget && !this.resultsTarget.contains(event.relatedTarget)) this.close()
  }

  close() {
    clearTimeout(this.timer)
    this.request?.abort()
    this.sequence++
    this.active = -1
    this.items = []
    this.resultsTarget.hidden = true
    for (const field of [this.localityTarget, this.postalCodeTarget]) {
      field.setAttribute("aria-expanded", "false")
      field.removeAttribute("aria-activedescendant")
    }
    this.statusTarget.textContent = ""
    this.postalStatusTarget.textContent = ""
  }
}
